import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostJson } from './client'
import type { ConfiguredField, FieldKind, PaletteField, SavedField } from './settingsEaseedeskFormBuilder'

/**
 * Settings → Easeedesk → Update form builder si Update form configuration.
 *
 * Perechile vechi: `settings/easeedesk/update_form_builder`,
 * `get_update_forms`, `create_update_form`, `remove_update_form`,
 * `edit_update_form`, `get_update_sortable_field`, `save_update_form`,
 * `update_form_configuration`, `assign_update_form`, `create_update_form_here`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 *
 * CAMPURILE au aceeasi forma ca la formularele de tichet, deci se folosesc
 * aceleasi tipuri si aceleasi randuri de ecran. Tabelele din baza sunt insa
 * altele, si coloana de vizibilitate se cheama acolo `visibility`, nu
 * `field_visibility` - de-aia capetele sunt separate.
 */

export type { ConfiguredField, FieldKind, PaletteField, SavedField }

export interface UpdateFormListEntry {
  ID: number
  form_name: string
  form_description: string
  fields: number
  custom_fields: number
  /** Tipurile de tichet care folosesc formularul. */
  used_by: string[]
}

export interface UpdateFormEditor {
  formDetails: { ID: number; form_name: string; form_description: string }
  configured_fields: ConfiguredField[]
  fields: PaletteField[]
  custom_fields: PaletteField[]
}

export interface UpdateFormConfiguration {
  title: string
  moduleUrl: string
  ticket_types: { ID: number; type_name: string; short_name: string; color_identifier: string }[]
  form_list: { ID: number; form_name: string }[]
  configuration: { ticket_type_id: number; form_id: number }[]
  /** Fereastra "formular nou pentru tipul asta" cere si dreptul constructorului. */
  canCreateForms: boolean
}

export function updateFormsShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'update_form_builder'],
    queryFn: () => apiGet<{ title: string; moduleUrl: string }>('/settings/easeedesk/update_form_builder'),
  })
}

export function updateFormListQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'update_form_builder', 'list'],
    queryFn: () => apiGet<{ form_list: UpdateFormListEntry[] }>('/settings/easeedesk/update_form_builder/list'),
  })
}

export function updateFormEditorQuery(form: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'update_form_builder', 'editor', form],
    queryFn: () => apiGet<UpdateFormEditor>(`/settings/easeedesk/update_form_builder/${form}`),
    enabled: form > 0,
  })
}

/** `get_update_sortable_field`: campul bifat, ca sa fie asezat pe loc. */
export function updatePaletteField(field: number, kind: FieldKind) {
  return apiGet<{ field: PaletteField }>(`/settings/easeedesk/update_form_builder/field/${field}/${kind}`)
}

export function createUpdateForm(form_name: string, form_description: string) {
  return apiPost<{ ok?: boolean; form?: { ID: number; form_name: string } }>(
    '/settings/easeedesk/update_form_builder',
    { form_name, form_description },
  )
}

export function removeUpdateForm(form: number, confirmed = false) {
  return apiPost<{ ok?: boolean; confirm?: boolean; used_by?: string[] }>(
    `/settings/easeedesk/update_form_builder/${form}/remove`,
    { confirmed: confirmed ? 1 : 0 },
  )
}

export function saveUpdateForm(form: number, form_name: string, form_description: string, fields: SavedField[]) {
  return apiPostJson<{ ok?: boolean }>(`/settings/easeedesk/update_form_builder/${form}`, {
    form_name,
    form_description,
    fields,
  })
}

export function updateFormConfigurationQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'update_form_configuration'],
    queryFn: () => apiGet<UpdateFormConfiguration>('/settings/easeedesk/update_form_configuration'),
  })
}

/** `form_id` zero inseamna "niciun formular" - randul gol din `<select>`. */
export function assignUpdateForm(ticket_type: number, form_id: number) {
  return apiPost<{ ok?: boolean }>('/settings/easeedesk/update_form_configuration/assign', { ticket_type, form_id })
}

/** Formular nou, facut si legat de tip dintr-o data. */
export function createUpdateFormHere(ticket_type: number, form_name: string) {
  return apiPost<{ ok?: boolean; form?: { ID: number; form_name: string } }>(
    '/settings/easeedesk/update_form_configuration',
    { ticket_type, form_name },
  )
}
